import { getUrl } from '../../config';
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Power } from "react-feather";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { QRCodeSVG } from 'qrcode.react';

import {
  Tooltip,
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";

import useAuth from "../../hooks/useAuth";

const IconButton = styled(MuiIconButton)`
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease-in-out;
  }
  border-radius: 8px;
  padding: 8px;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: ${(props) =>
      props.theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(0, 0, 0, 0.04)"};
    color: ${(props) => props.theme.palette.primary.main};
    svg {
      transform: scale(1.05);
    }
  }
`;

const QRCodeContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const QRCodeWrapper = styled(Box)`
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

function NavbarUserDropdown() {
  const [anchorMenu, setAnchorMenu] = React.useState(null);
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const [newErpModalOpen, setNewErpModalOpen] = useState(false);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastDuration, setBroadcastDuration] = useState("60");
  const [broadcastError, setBroadcastError] = useState(null);
  const [broadcastSuccess, setBroadcastSuccess] = useState(null);
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const [newErpName, setNewErpName] = useState("");
  const [qrCode, setQrCode] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [erpError, setErpError] = useState(null);
  const [erpSuccess, setErpSuccess] = useState(null);
  const [isCreatingErp, setIsCreatingErp] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axios.get(getUrl() + "/api/user/getMe");
        setIsTwoFactorEnabled(response.data.user.twoFactorEnabled || false);
        setIsSuperAdmin(response.data.user.isSuperAdmin || false);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, []);


  const toggleMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const handleSignOut = async () => {
    await signOut();
    localStorage.removeItem("selectedErp");
    navigate("/auth/sign-in");
  };

  const openModal2FA = async () => {
    try {
      const response = await axios.get(getUrl() + "/api/auth/setup-two-factor");
      setQrCode(response.data.otpauth);
      setTwoFactorModalOpen(true);
      setError(null);
      setSuccess(null);
    } catch (error) {
      setError("Erreur lors de la configuration de la 2FA");
    }
  };

  const handleEnable2FA = async () => {
    try {
      const response = await axios.post(getUrl() + "/api/auth/enable-two-factor", {
        code: verificationCode
      });
      setSuccess("2FA activée avec succès");
      setTimeout(() => {
        setTwoFactorModalOpen(false);
      }, 2000);
    } catch (error) {
      setError("Code 2FA invalide");
    }
  };


  const handleCreateNewErp = async () => {
    try {
      setIsCreatingErp(true);
      setErpError(null);
      setErpSuccess(null);
      const response = await axios.post(getUrl() + "/api/erp/create", {
        name: newErpName
      });
      handleErpChange(response.data.data.name, response.data.data.id);
    } catch (error) {
      setErpError(error.response?.data?.message || "Erreur lors de la création de l'ERP");
    } finally {
      setIsCreatingErp(false);
    }
  };

  const handleErpChange = async (name, id) => {
    closeMenu();
    const erpName = name;

    // Créer dynamiquement un overlay HTML au-dessus de tout
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = 0;
    overlay.style.left = 0;
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.backgroundColor = '#1B2635';
    overlay.style.zIndex = 99999;
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.style.fontFamily = 'Arial, sans-serif';

    // Ajoute le contenu
    overlay.innerHTML = `
    <h1 style="font-size: 24px; margin-bottom: 20px; color: white;">Création de l'ERP : ${erpName}</h1>
    <div id="progress-bar" style="width: 50%; height: 10px; background-color: #ddd; border-radius: 5px; overflow: hidden; margin-bottom: 20px;">
        <div id="progress-fill" style="
            width: 0%;
            height: 100%;
            background: linear-gradient(270deg, #2196f3, #1a237e, #2196f3);
            background-size: 600% 600%;
            animation: gradientAnimation 3s ease infinite;
            transition: width 0.5s ease;
            border-radius: 5px;
        "></div> 
    </div>
    <p id="progress-text" style="font-size: 18px; color: #407AD6;">Initialisation...</p>
`;
    document.body.appendChild(overlay);

    // Animation de la barre de progression
    let progress = 0;
    const messages = [
      'Initialisation de la création...',
      'Création des éléments...',
      'Configuration des paramètres...',
      'Finalisation de la création...'
    ];

    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        clearInterval(interval);
        navigate(`/`);
        window.location.reload();
      } else {
        const fill = document.getElementById('progress-fill');
        const text = document.getElementById('progress-text');
        if (fill) fill.style.width = `${Math.min(progress, 100)}%`;
        if (text) {
          if (progress < 25) text.innerText = messages[0];
          else if (progress < 50) text.innerText = messages[1];
          else if (progress < 75) text.innerText = messages[2];
          else text.innerText = messages[3];
        }
      }
    }, 300);
    window.localStorage.setItem("erpId", id);
  };

  const handleSendBroadcast = async () => {
    try {
      setIsSendingBroadcast(true);
      setBroadcastError(null);
      setBroadcastSuccess(null);

      const response = await axios.post(getUrl() + "/api/erp/broadcast", {
        message: broadcastMessage,
        duration: broadcastDuration
      });

      if (response.data.status === 'success') {
        setBroadcastModalOpen(false);
        setBroadcastMessage("");
        setBroadcastDuration("60");
      } else {
        setBroadcastError(response.data.message || "Erreur lors de l'envoi du message");
      }
    } catch (error) {
      setBroadcastError(error.response?.data?.message || "Erreur lors de l'envoi du message");
    } finally {
      setIsSendingBroadcast(false);
    }
  };

  return (
    <React.Fragment>
      <Tooltip title={t("Account")}>
        <IconButton
          aria-owns={Boolean(anchorMenu) ? "menu-appbar" : undefined}
          aria-haspopup="true"
          onClick={toggleMenu}
          color="inherit"
          size="large"
        >
          <Power />
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
      >
        <MenuItem onClick={closeMenu}>{t("Profile")}</MenuItem>
        {!isTwoFactorEnabled && (
          <MenuItem onClick={() => {
            closeMenu();
            openModal2FA();
          }}>{t("2FA")}</MenuItem>
        )}

        {isSuperAdmin && (
          <>
            <MenuItem onClick={() => {
              closeMenu();
              setNewErpModalOpen(true);
            }}>{t("Nouvelle ERP")}</MenuItem>
            <MenuItem onClick={() => {
              closeMenu();
              setBroadcastModalOpen(true);
            }}>{t("Broadcast")}</MenuItem>
          </>
        )}
        <MenuItem onClick={handleSignOut}>{t("Sign out")}</MenuItem>
      </Menu>

      <Dialog
        open={twoFactorModalOpen}
        onClose={() => setTwoFactorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configuration 2FA</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {success && (
            <Typography color="success" sx={{ mb: 2 }}>
              {success}
            </Typography>
          )}
          {qrCode && (
            <QRCodeContainer>
              <QRCodeWrapper>
                <QRCodeSVG
                  value={qrCode}
                  size={200}
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: "/logo.png",
                    height: 40,
                    width: 40,
                    excavate: true,
                  }}
                />
              </QRCodeWrapper>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                Scannez ce QR code avec Google Authenticator
              </Typography>
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                Ou entrez manuellement le code secret
              </Typography>
            </QRCodeContainer>
          )}
          <TextField
            fullWidth
            label="Code de vérification"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Entrez le code à 6 chiffres généré par l'application"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFactorModalOpen(false)}>Annuler</Button>
          <Button onClick={handleEnable2FA} variant="contained" color="primary">
            Activer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newErpModalOpen}
        onClose={() => {
          setNewErpModalOpen(false);
          setNewErpName("");
          setErpError(null);
          setErpSuccess(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Créer une nouvelle ERP</DialogTitle>
        <DialogContent>
          {erpError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {erpError}
            </Typography>
          )}
          {erpSuccess && (
            <Typography color="success" sx={{ mb: 2 }}>
              {erpSuccess}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Nom de l'ERP"
            type="text"
            fullWidth
            value={newErpName}
            onChange={(e) => setNewErpName(e.target.value)}
            variant="outlined"
            error={!!erpError}
            disabled={isCreatingErp}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewErpModalOpen(false);
              setNewErpName("");
              setErpError(null);
              setErpSuccess(null);
            }}
            disabled={isCreatingErp}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreateNewErp}
            variant="contained"
            color="primary"
            disabled={!newErpName.trim() || isCreatingErp}
          >
            {isCreatingErp ? "Création..." : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={broadcastModalOpen}
        onClose={() => {
          setBroadcastModalOpen(false);
          setBroadcastMessage("");
          setBroadcastDuration("60");
          setBroadcastError(null);
          setBroadcastSuccess(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Envoyer un message broadcast</DialogTitle>
        <DialogContent>
          {broadcastError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {broadcastError}
            </Typography>
          )}
          {broadcastSuccess && (
            <Typography color="success" sx={{ mb: 2 }}>
              {broadcastSuccess}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            variant="outlined"
            error={!!broadcastError}
            disabled={isSendingBroadcast}
            sx={{ mt: 2 }}
            placeholder="Entrez votre message broadcast ici..."
          />
          <TextField
            margin="dense"
            label="Durée d'affichage (secondes)"
            type="number"
            fullWidth
            value={broadcastDuration}
            onChange={(e) => setBroadcastDuration(e.target.value)}
            variant="outlined"
            error={!!broadcastError}
            disabled={isSendingBroadcast}
            sx={{ mt: 2 }}
            inputProps={{ min: 1, max: 604800 }}
            helperText="Durée d'affichage du message en secondes (1-604800)"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBroadcastModalOpen(false);
              setBroadcastMessage("");
              setBroadcastDuration("60");
              setBroadcastError(null);
              setBroadcastSuccess(null);
            }}
            disabled={isSendingBroadcast}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSendBroadcast}
            variant="contained"
            color="primary"
            disabled={!broadcastMessage.trim() || isSendingBroadcast}
          >
            {isSendingBroadcast ? "Envoi..." : "Envoyer"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default NavbarUserDropdown;